import { SelectChangeEvent } from "@mui/material";
import React, {
  createContext,
  useState,
  useContext,
  ReactNode,
  useEffect,
} from "react";
import { AppDispatch, RootState } from "../../store/store";
import { useDispatch, useSelector } from "react-redux";
import { ProjectTrain } from "../../types/interface/ProjectTrain.interface";
import {
  addNewProjectTrain,
  deleteProjectTrain,
  fetchLabelCount,
  fetchModels,
  fetchProjectTrain,
  fetchProjectTrainById,
  fetchTrainingStatus,
  clearModelData,
  fetchProjectTrainAll,
} from "../../store/slice/ProjectTrainSlice";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";

export interface Model {
  id: string; // UUID
  name: string;
  description: string;
  supportEdgeDevice: boolean;
  taskTypeId: string; // UUID
  taskType: string | null; // Assuming it can be null or a string
}
// Define the shape of the context data
// Define the shape of the context data
interface TrainContextType {
  id: string | undefined;
  labels: string[];
  data: number[];
  minDistance: number;
  value1: number | number[];
  setValue1: React.Dispatch<React.SetStateAction<number | number[]>>;
  selectedIndex: string | undefined;
  setSelectedIndex: React.Dispatch<React.SetStateAction<string | undefined>>;
  detailModels: Model[];
  setDetailModels: React.Dispatch<React.SetStateAction<Model[]>>;
  modelsList: ProjectTrain[];
  setModelsList: React.Dispatch<React.SetStateAction<ProjectTrain[]>>;
  handleCheck: (event: React.ChangeEvent<HTMLInputElement>) => void; // Update to function signature
  handleChange22: (event: React.ChangeEvent<HTMLInputElement>) => void; // Update to function signature
  handleChange: (event: SelectChangeEvent) => void; // Fix type here
  dataIdDetails: DataIdDetails;
  setDataIdDetails: React.Dispatch<React.SetStateAction<DataIdDetails>>;
  handleModelQuality: (value: string) => void;
  handleSubmit: () => void;
  handleDelete: (id: any) => void;
  handleRename: (id: any) => void;
  loading: Boolean;
  statusTrain: String | null;
  setStatusTrain: React.Dispatch<React.SetStateAction<String | null>>;
  isDeleteModalOpen: boolean;
  pendingDeleteId: string | null;
  closeDeleteModal: () => void;
  confirmDelete: () => void;
  isDeleting: boolean;
  isSubmitting: boolean;
  setIsSubmitting:React.Dispatch<React.SetStateAction<boolean>>;
}

// Create the context with a default value
const TrainContext = createContext<TrainContextType | undefined>(undefined);
interface CheckState {
  checkedA: boolean;
  checkedB: boolean;
  checkedC: boolean;
  checkedD: boolean;
  checkedE: boolean;
}

interface Augmentations {
  id: string;
  horizontalFlip: boolean;
  verticalFlip: boolean;
  randomRotate: boolean;
  randomCrop: boolean;
  randomShear: boolean;
  randomScale: boolean;
  resize: boolean;
  randomRotate90: boolean;
  randomBrightnessContrast: boolean;
  gaussNoise: boolean;
  hueSaturationValue: boolean;
  blur: boolean;
  equalize: boolean;
  randomGamma: boolean;
  sharpen: boolean;
  toGray: boolean;
  toSepia: boolean;
  shiftScaleRotate: boolean;
  opticalDistortion: boolean;
  gridDistortion: boolean;
  transpose: boolean;
}

interface SplitDataset {
  id: string;
  train: number;
  validate: number;
  test: number;
}

interface AdvanceSetting {
  modelTypeId: string;
  modelQuality: string;
  augmentations: Augmentations;
  splitDataset: SplitDataset;
}

export interface DataIdDetails {
  id: string;
  modelUrl: string | null;
  name: string;
  description: string;
  epochs: number;
  projectId: string;
  advanceSetting: AdvanceSetting;
  progress?: String;
  status?: string;
  isPublished?:boolean
}

// Define the type for the provider's props
interface TrainProviderProps {
  children: ReactNode;
}
interface Image {
  //   id: string;
}

export const initialDataIdDetails: DataIdDetails = {
  id: "",
  modelUrl: null,
  name: "",
  description: "",
  epochs: 150,
  projectId: "",
  advanceSetting: {
    modelTypeId: "",
    modelQuality: "Medium",
    augmentations: {
      id: "",
      horizontalFlip: true,
      randomRotate: true,
      randomCrop: true,
      randomShear: true,
      randomScale: true,
      resize: true,
      randomBrightnessContrast: true,
      hueSaturationValue: true,
      blur: true,
      opticalDistortion: true,
      gridDistortion: true,
      randomGamma: true,
      verticalFlip: false,
      randomRotate90: false,
      gaussNoise: false,
      equalize: false,
      sharpen: false,
      toGray: false,
      toSepia: false,
      shiftScaleRotate: false,
      transpose: false,
    },
    splitDataset: {
      id: "",
      train: 0.8,
      validate: 0.1,
      test: 0.1,
    },
  },
};
// Create the provider component
export const TrainProvider: React.FC<TrainProviderProps> = ({ children }) => {
  const labels = ["Class A", "Class B", "Class C", "Class D"];
  const data = [65, 59, 80, 81];
  const dispatch: AppDispatch = useDispatch();
  const minDistance = 0;
  const [isDeleting, setIsDeleting] = useState(false);
  // Change the initial state to string | undefined
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [pendingDeleteId, setPendingDeleteId] = useState(null);
  const [value1, setValue1] = React.useState<number | number[]>([20, 37]); // Adjust state initialization
  const [dataIdDetails, setDataIdDetails] =
    useState<DataIdDetails>(initialDataIdDetails);
  const [statusTrain, setStatusTrain] = useState<String | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [selectedIndex, setSelectedIndex] = useState<string | undefined>(
    undefined
  );

  const [detailModels, setDetailModels] = useState<Model[]>([]);

  const {
    dataModels,
    error,
    models,
    selectedProjectTrain,
    labelCount,
    loading,
  } = useSelector((state: RootState) => state.projectTrain);
  const [modelsList, setModelsList] = useState<ProjectTrain[]>([]);

  const { id } = useParams();

  const handleCheck = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = event.target; // Destructure the name and checked properties from the event target

    setDataIdDetails((prevState) => ({
      ...prevState,
      advanceSetting: {
        ...prevState.advanceSetting,
        augmentations: {
          ...prevState.advanceSetting.augmentations,
          [name]: checked, // Update the relevant augmentation based on the checkbox name
        },
      },
    }));
  };

  const handleDelete = (idss: any) => {
    setPendingDeleteId(idss);
    setIsDeleteModalOpen(true);
    localStorage.setItem("popupOpen", true.toString());
  };

  const closeDeleteModal = () => {
    setPendingDeleteId(null);
    setIsDeleteModalOpen(false);
    localStorage.setItem("popupOpen", false.toString());
  };

  const confirmDelete = () => {
    if (pendingDeleteId) {
      setIsDeleting(true);
      dispatch(deleteProjectTrain(pendingDeleteId)) // Dispatch delete action
        .unwrap() // Unwrap the promise to handle success or failure
        .then(() => {
          if (id) {
            // dispatch(fetchProjectTrain(id)); // Fetch updated project data
            dispatch(fetchProjectTrainAll(id));
            toast.success("Project train deleted successfully!");
            setSelectedIndex(undefined)
          } else {
            toast.error("Project id is undefined, cannot fetch project.");
          }
        })
        .catch((error) => {
          const errorMessage =
            error?.message ||
            error ||
            "Failed to delete project train. Try again.";
          toast.error(errorMessage);
        })
        .finally(() => {
          setIsDeleting(false);
          closeDeleteModal(); // Close the modal after completion
        });
    }
  };

  const handleRename = (id: any) => {};

  // useEffect(() => {
  //   if (id) {
  //     dispatch(fetchProjectTrain(id));
  //     dispatch(fetchModels(id));
  //   }
  // }, [id, dispatch]);

  useEffect(() => {
    if (selectedProjectTrain) {
      setDataIdDetails(selectedProjectTrain);
    }
  }, [selectedProjectTrain]);

  // const handleSubmit = async () => {
  //   try {
  //     const result = await dispatch(
  //       addNewProjectTrain({ ...dataIdDetails, projectId: id || "" })
  //     )
  //       .unwrap()
  //       .then((result) => {
  //         // Only update modelsList on success
  //         const chkss = modelsList.shift();

  //         setModelsList(chkss ? [...modelsList] : []);

  //         setSelectedIndex(result.id);
  //         if (id) {
  //           dispatch(fetchProjectTrain(id));
  //           dispatch(fetchTrainingStatus(id))
  //             .unwrap()
  //             .then((result) => setStatusTrain(result));
  //         }
  //       });
  //     toast.success(
  //       `Project ${dataIdDetails.name} has commenced for training!`
  //     );
  //   } catch (error: any) {
  //     // Show the raw error message returned from the thunk
  //     const errorMessage = error as string;
  //     console.log("errorMessage-----------", errorMessage);
  //     toast.error(errorMessage);
  //     // No modelsList mutation here, so it's not affected by errors
  //     // setDataIdDetails(initialDataIdDetails);
  //     // setSelectedIndex(undefined);
  //   }
  // };

  const handleSubmit = async () => {
    try {
      setIsSubmitting(true); // Disable the button
      const result = await dispatch(
        addNewProjectTrain({ ...dataIdDetails, projectId: id || "" })
      )
        .unwrap()
        .then((result) => {
          const chkss = modelsList.shift();
          setDataIdDetails(initialDataIdDetails)
          setModelsList(chkss ? [...modelsList] : []);
          setSelectedIndex(result.id);
          if (id) {
            dispatch(fetchProjectTrainAll(id));
          }
        });

      toast.success(
        `Project ${dataIdDetails.name} has commenced for training!`
      );
    } catch (error: any) {
      // Try to extract error message from payload.detail or detail
      let errorMessage = "An error occurred";
      if (error && typeof error === "object") {
        if ("payload" in error && error.payload?.detail) {
          errorMessage = error.payload.detail;
        } else if ("detail" in error) {
          errorMessage = error.detail;
        } else if ("message" in error) {
          errorMessage = error.message;
        }
      } else if (typeof error === "string") {
        errorMessage = error;
      }
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false); // Re-enable the button after completion
    }
  };
  useEffect(() => {
    if (selectedIndex) {
      if (/^[0]\.\d+/.test(selectedIndex)) {
        setDataIdDetails(initialDataIdDetails);
      } else {
        dispatch(fetchProjectTrainById(selectedIndex));
      }
    }
  }, [selectedIndex]);

  const handleChange22 = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(event.target.value, 10);

    if (value.toString().length > 1 && value < 6) {
      return; // Prevent further action if value is less than 6
    }

    if (value > 250) {
      return; // Prevent further action if value is more than 250
    }

    // If within range, proceed with your logic
    setDataIdDetails({ ...dataIdDetails, epochs: value });
  };

  const handleModelQuality = (value: string) => {
    setDataIdDetails({
      ...dataIdDetails,
      advanceSetting: {
        ...dataIdDetails.advanceSetting,
        modelQuality: value,
      },
    });
  };

  useEffect(() => {
    setDataIdDetails(initialDataIdDetails);
    setSelectedIndex(undefined);
  }, []);

  useEffect(() => {
    if (error) {
      setModelsList([]);
      return;
    }
  
    if (modelsList.length && modelsList[0]?.trainingId?.startsWith("0.")) {
      setModelsList([modelsList[0], ...dataModels]);
    } else if (dataModels.length) {
      setModelsList(dataModels);
    } else {
      setModelsList([]);
    }
  }, [dataModels, error]);
  

  useEffect(() => {
    if (models.length) {
      setDetailModels(models);
      setDataIdDetails({ ...dataIdDetails, projectId: id ? id : "string" });
    }
  }, [models]);

  const handleChange = (event: SelectChangeEvent) => {
    setDataIdDetails({
      ...dataIdDetails,
      advanceSetting: {
        ...dataIdDetails.advanceSetting,
        modelTypeId: event.target.value,
      },
    });
  };
  // Context value that is passed to consumers
  const value = {
    id,
    labels,
    data,
    minDistance,
    value1,
    setValue1,
    selectedIndex,
    setSelectedIndex,
    detailModels,
    setDetailModels, // Correct setter type
    modelsList,
    setModelsList,
    handleCheck,
    handleChange22,
    handleChange, // Add handleChange to the value
    dataIdDetails,
    setDataIdDetails,
    handleModelQuality,
    handleSubmit,
    handleDelete,
    handleRename,
    loading,
    statusTrain,
    setStatusTrain,
    isDeleteModalOpen,
    pendingDeleteId,
    confirmDelete,
    closeDeleteModal,
    isDeleting,
    isSubmitting,
    setIsSubmitting
  };

  return (
    <TrainContext.Provider value={value}>{children}</TrainContext.Provider>
  );
};

// Create a custom hook to use the TrainContext
export const useTrain = (): TrainContextType => {
  const context = useContext(TrainContext);
  if (context === undefined) {
    throw new Error("useTrain must be used within a TrainProvider");
  }
  return context;
};
